import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import DropdownMenu, {
  DropdownMenuItem,
} from "../../../components/dropdown-menu/DropdownMenu";
import {
  type LibraryFolderItem,
  type LibrarySection,
} from "../../../app/services/library";
import useCollapsedSections from "../../../hooks/useCollapsedSections";

/**
 * ライブラリページ左側の「ライブラリ（セクション） > 階層フォルダ」ツリー。
 * NotesSidebar と同じ見た目・操作感で、フォルダを選択すると右側の一覧が
 * そのフォルダ配下のみに絞り込まれる（スコープ表示）。
 *
 * - セクション見出し行はノートのセクション見出しと同じ配色（バナーカラー対応）
 *   で、クリック = セクションルート選択（絞り込みなし）を兼ねる。
 *   右端の3点メニューに「設定」「フォルダを作成」を格納する。
 *   先頭の矢印でセクション全体を開閉できる（開閉状態は localStorage）
 * - フォルダ行: クリックで選択（子があれば展開）、矢印クリックで開閉のみ。
 *   右端の3点メニューからファイルリスト側のフォルダ行と同じ操作
 *   （一括DL / 共有 / 作成 / リネーム / ルートへ移動 / 削除）を行える
 * - セクション設定は canManage（master_admin）またはセクションオーナー
 */

/** フォルダ行3点メニューの操作種別（Library 側で FileBrowser へ中継する） */
export type SidebarFolderAction =
  | "createFolder"
  | "renameFolder"
  | "deleteFolder"
  | "shareFolder"
  | "downloadFolder"
  | "transcribeFolder"
  | "moveFolderToRoot";

const SECTION_HEADER_HEIGHT = 36;

const SidebarWrapper = styled.nav`
  width: 100%;
  height: 100%;
  overflow-y: auto;

  @media (max-width: 767px) {
    border-bottom: 1px solid var(--soremo-border);
  }
`;

/* .u-col は align-items: flex-start で子が内容幅に縮むため、
   行を常にサイドバー全幅へストレッチさせる専用コンテナを使う */
const SectionsList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  width: 100%;
`;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
`;

/**
 * セクション見出し行。NotesSidebar と同じ配色ルール:
 * バナーカラー指定時は `banner-color${N}` クラス（文字色はクラスの
 * `div` セレクタに委ねるため、内容は必ず inner div でラップする）、
 * 未指定時は `--soremo-border` の単色。
 *
 * 行クリック = セクションルート選択（「すべてのファイル」相当）を兼ねる。
 * 選択中はバナー色の上でも判別できるよう左端に青バーを重ねる。
 */
const SectionHeaderRow = styled.div.attrs<{
  $bannerColor?: string;
  $selected: boolean;
}>(({ $bannerColor }) => ({
  className: $bannerColor ? `banner-color${$bannerColor}` : undefined,
}))<{ $bannerColor?: string; $selected: boolean }>`
  width: 100%;
  height: ${SECTION_HEADER_HEIGHT}px;
  border-bottom: 1px solid var(--soremo-border);
  cursor: pointer;
  ${({ $bannerColor }) =>
    $bannerColor ? "" : "background: var(--soremo-border);"}
  ${({ $selected }) =>
    $selected ? "box-shadow: inset 3px 0 0 var(--soremo-blue);" : ""}

  /* banner-color クラスの \`div { color: ... }\` を受けて子へ継承させる */
  .section-header-inner {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 100%;
    padding: 0 8px;

    h3,
    span,
    button,
    .material-symbols-rounded {
      color: inherit;
    }
  }

  .section-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* 開閉矢印。フォルダ行の矢印と同じ行頭スロット 16px で、開いているときは
     90° 回転して下向きにする。行クリック（選択）とは分離する */
  .section-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 16px;
    height: 20px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;

    .material-symbols-rounded {
      font-size: 20px;
      transition: transform 0.15s ease;
    }

    &.open .material-symbols-rounded {
      transform: rotate(90deg);
    }
  }

  /* 3点メニューは行クリック（選択）と分離する */
  .section-menu {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  @media (hover: hover) {
    &:hover {
      filter: brightness(0.97);
    }
  }
`;

/**
 * ツリー行（フォルダ）。NotesSidebar のフォルダ行と同じ振る舞いで、
 * 階層に応じて左に段下げする。行頭アイコンは置かない（矢印の有無と
 * インデントで階層が分かるため。ファイルリスト側とも揃える）。
 * 3点メニュー（button）を内包するため button ではなく div にする。
 */
const TreeRow = styled.div<{ $selected: boolean; $depth: number }>`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  height: 40px;
  /* NotesSidebar のフォルダ行と同じ基準（ルート 8px + 階層 16px 刻み） */
  padding: 0 8px 0 ${({ $depth }) => 8 + $depth * 16}px;
  border-bottom: 1px solid var(--soremo-border);
  background: ${({ $selected }) =>
    $selected ? "var(--soremo-border)" : "white"};
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s ease;

  .row-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--soremo-gray);
  }

  /* 開閉を示す矢印。開いたら 90° 回転して下向きにする。
     幅はスペーサー・ブックマークアイコンと同じ 16px に揃え、
     行頭スロット(16px) + gap(4px) でテキスト開始位置を統一する */
  .row-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 16px;
    height: 20px;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;

    .material-symbols-rounded {
      font-size: 20px;
      color: var(--soremo-light-gray);
      transition: transform 0.15s ease;
    }

    &.open .material-symbols-rounded {
      transform: rotate(90deg);
    }
  }

  /* 子なしフォルダはインデントを揃えるためのスペーサー */
  .row-arrow-spacer {
    width: 16px;
    flex-shrink: 0;
  }

  /* 3点リーダー（DropdownMenu）。タッチ端末でも操作できるよう常時表示 */
  .folder-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  @media (hover: hover) {
    &:hover {
      background: ${({ $selected }) =>
        $selected ? "var(--soremo-border)" : "var(--soremo-background)"};
    }
  }
`;

const AddSectionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px;
  border: none;
  background: transparent;
  color: var(--soremo-light-gray);
  cursor: pointer;
  text-align: left;

  .material-symbols-rounded {
    font-size: 16px;
  }

  @media (hover: hover) {
    &:hover {
      color: var(--soremo-blue);
      background: var(--soremo-background);
    }
  }
`;

// ── フォルダツリー構築 ────────────────────────────────────────────────

interface SidebarFolderNode {
  id: string;
  name: string;
  depth: number;
  /** 元のフォルダ情報（3点メニューの操作対象を親へ通知するのに使う） */
  item: LibraryFolderItem;
  children: SidebarFolderNode[];
}

const buildFolderTree = (folders: LibraryFolderItem[]): SidebarFolderNode[] => {
  const ids = new Set(folders.map((f) => f.id));
  const byParent = new Map<string | null, LibraryFolderItem[]>();
  folders.forEach((f) => {
    // 親が見えない（権限外等）フォルダはルート直下として扱う
    const parent = f.parent_id && ids.has(f.parent_id) ? f.parent_id : null;
    const list = byParent.get(parent) ?? [];
    list.push(f);
    byParent.set(parent, list);
  });
  const build = (parentId: string | null, depth: number): SidebarFolderNode[] =>
    (byParent.get(parentId) ?? [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, "ja"))
      .map((f) => ({
        id: f.id,
        name: f.name,
        depth,
        item: f,
        children: build(f.id, depth + 1),
      }));
  return build(null, 0);
};

const OPEN_FOLDERS_STORAGE_KEY = "library-open-folders";

const loadOpenFolders = (): Set<string> => {
  try {
    const raw = localStorage.getItem(OPEN_FOLDERS_STORAGE_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};

// ── サイドバー本体 ───────────────────────────────────────────────────

export interface LibrarySidebarProps {
  sections: LibrarySection[];
  selectedSectionId: string | null;
  /** 選択中フォルダ（null = セクションルート = 絞り込みなし） */
  selectedFolderId: string | null;
  /** 「ブックマーク」ページを表示中のセクション（null = 非表示） */
  bookmarksSectionId?: string | null;
  /** セクションの作成・権限設定変更ができるか（master_admin） */
  canManage: boolean;
  /**
   * まだ開閉していないセクションを閉じた状態で表示する。グローバルメニューの
   * 「共有ライブラリ」で複数ライブラリを並べるとき、一覧性を優先する用途。
   */
  defaultSectionsCollapsed?: boolean;
  // eslint-disable-next-line no-unused-vars
  onSelect: (sectionId: string, folderId: string | null) => void;
  /** セクション先頭の「ブックマーク」ページを開く */
  // eslint-disable-next-line no-unused-vars
  onSelectBookmarks?: (sectionId: string) => void;
  // eslint-disable-next-line no-unused-vars
  onOpenSectionSettings: (section: LibrarySection) => void;
  /** セクション行メニューの「フォルダを作成」（セクションルート直下） */
  // eslint-disable-next-line no-unused-vars
  onCreateFolder: (section: LibrarySection) => void;
  /** フォルダ行3点メニューの操作（FileBrowser のフォルダメニューと同じ機能） */
  onFolderAction: (
    // eslint-disable-next-line no-unused-vars
    section: LibrarySection,
    // eslint-disable-next-line no-unused-vars
    folder: LibraryFolderItem,
    // eslint-disable-next-line no-unused-vars
    action: SidebarFolderAction,
  ) => void;
  onCreateSection: () => void;
}

const LibrarySidebar: React.FC<LibrarySidebarProps> = ({
  sections,
  selectedSectionId,
  selectedFolderId,
  bookmarksSectionId = null,
  canManage,
  defaultSectionsCollapsed = false,
  onSelect,
  onSelectBookmarks,
  onOpenSectionSettings,
  onCreateFolder,
  onFolderAction,
  onCreateSection,
}) => {
  const [openFolders, setOpenFolders] = useState<Set<string>>(loadOpenFolders);

  const persistOpenFolders = useCallback((next: Set<string>) => {
    try {
      localStorage.setItem(OPEN_FOLDERS_STORAGE_KEY, JSON.stringify([...next]));
    } catch {
      /* localStorage が使えない環境では開閉状態を永続化しない */
    }
  }, []);

  const toggleFolder = useCallback(
    (folderId: string) => {
      setOpenFolders((prev) => {
        const next = new Set(prev);
        if (next.has(folderId)) next.delete(folderId);
        else next.add(folderId);
        persistOpenFolders(next);
        return next;
      });
    },
    [persistOpenFolders],
  );

  const expandFolder = useCallback(
    (folderId: string) => {
      setOpenFolders((prev) => {
        if (prev.has(folderId)) return prev;
        const next = new Set(prev).add(folderId);
        persistOpenFolders(next);
        return next;
      });
    },
    [persistOpenFolders],
  );

  const {
    isCollapsed: isSectionCollapsed,
    toggle: toggleSection,
    expand: expandSection,
  } = useCollapsedSections(
    "library-collapsed-sections",
    defaultSectionsCollapsed,
  );

  // 閉じたセクションの中の行（フォルダ / ブックマーク）が選択されたら
  // （ファイル一覧からのフォルダ移動等）、そのセクションを開いて選択行を
  // 見せる。見出し行の選択（セクションルート）は見出しが見えているので
  // 開かない。開いた後に閉じ直せるよう、同じ選択に対しては一度だけ行う。
  // 表示直後の選択（URL 直指定）では開かない（保存した開閉状態を優先）
  const autoExpandedForRef = useRef<string | null>(null);
  const initialSelectionSeenRef = useRef(false);
  useEffect(() => {
    const target = bookmarksSectionId ?? (selectedFolderId && selectedSectionId);
    if (!target) {
      if (selectedSectionId) initialSelectionSeenRef.current = true;
      return;
    }
    const selectionKey = bookmarksSectionId
      ? `bookmarks:${bookmarksSectionId}`
      : `folder:${selectedFolderId}`;
    if (autoExpandedForRef.current === selectionKey) return;
    autoExpandedForRef.current = selectionKey;
    if (!initialSelectionSeenRef.current) {
      initialSelectionSeenRef.current = true;
      return;
    }
    expandSection(target);
  }, [bookmarksSectionId, selectedFolderId, selectedSectionId, expandSection]);

  const treesBySection = useMemo(
    () =>
      new Map(
        sections.map((section) => [
          section.id,
          buildFolderTree(section.folders),
        ]),
      ),
    [sections],
  );

  /**
   * フォルダ行の3点メニュー。ファイルリスト（FileBrowser）のフォルダ行
   * メニューと同じ構成で、実処理は Library 側で FileBrowser へ中継する。
   */
  const buildFolderMenuItems = (
    section: LibrarySection,
    node: SidebarFolderNode,
  ): DropdownMenuItem[] => {
    const items: DropdownMenuItem[] = [
      {
        label: "一括ダウンロード",
        icon: "download",
        onClick: () => onFolderAction(section, node.item, "downloadFolder"),
      },
      {
        label: "一括文字起こし",
        icon: "speech_to_text",
        onClick: () => onFolderAction(section, node.item, "transcribeFolder"),
      },
    ];
    if (section.can_edit) {
      items.push(
        {
          label: "共有",
          icon: "share",
          onClick: () => onFolderAction(section, node.item, "shareFolder"),
        },
        {
          label: "フォルダを作成",
          icon: "create_new_folder",
          onClick: () => onFolderAction(section, node.item, "createFolder"),
        },
        {
          label: "リネーム",
          icon: "edit",
          onClick: () => onFolderAction(section, node.item, "renameFolder"),
        },
      );
      if (node.item.parent_id !== null) {
        items.push({
          label: "ルートへ移動",
          icon: "drive_file_move",
          onClick: () =>
            onFolderAction(section, node.item, "moveFolderToRoot"),
        });
      }
      items.push({
        label: "フォルダを削除",
        icon: "delete",
        onClick: () => onFolderAction(section, node.item, "deleteFolder"),
      });
    }
    return items;
  };

  const renderFolderRows = (
    section: LibrarySection,
    nodes: SidebarFolderNode[],
  ): React.ReactNode =>
    nodes.map((node) => {
      const isOpen = openFolders.has(node.id);
      const isSelected =
        section.id === selectedSectionId && node.id === selectedFolderId;
      const menuItems = buildFolderMenuItems(section, node);
      const selectFolder = () => {
        onSelect(section.id, node.id);
        // 子があるフォルダは選択と同時に展開して中身を見せる
        if (node.children.length > 0) expandFolder(node.id);
      };
      return (
        <React.Fragment key={node.id}>
          <TreeRow
            $selected={isSelected}
            $depth={node.depth}
            role="button"
            tabIndex={0}
            onClick={selectFolder}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                selectFolder();
              }
            }}
          >
            {node.children.length > 0 ? (
              <span
                className={`row-arrow${isOpen ? " open" : ""}`}
                role="button"
                aria-label={`${node.name} を${isOpen ? "折りたたむ" : "展開する"}`}
                onClick={(e) => {
                  // 矢印は開閉のみ（選択は行クリック側）
                  e.stopPropagation();
                  toggleFolder(node.id);
                }}
              >
                <span className="material-symbols-rounded">chevron_right</span>
              </span>
            ) : (
              <span className="row-arrow-spacer" />
            )}
            <span className="row-title bodytext-13 u-line-height-150">
              {node.name}
            </span>
            {menuItems.length > 0 && (
              <span
                className="folder-actions"
                // メニュー操作で行クリック（選択）を発火させない
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                aria-label={`${node.name} の操作メニュー`}
              >
                <DropdownMenu items={menuItems} size="small" />
              </span>
            )}
          </TreeRow>
          {isOpen && renderFolderRows(section, node.children)}
        </React.Fragment>
      );
    });

  return (
    <SidebarWrapper aria-label="ライブラリ一覧">
      <SectionsList>
        {sections.map((section) => {
          const tree = treesBySection.get(section.id) ?? [];
          const bookmarksSelected = section.id === bookmarksSectionId;
          const rootSelected =
            section.id === selectedSectionId &&
            selectedFolderId === null &&
            !bookmarksSelected;
          const collapsed = isSectionCollapsed(section.id);

          // 右端3点メニュー: 設定（管理者・オーナー） / フォルダを作成（editor）
          const menuItems: DropdownMenuItem[] = [];
          if (canManage || section.can_manage_roles) {
            menuItems.push({
              label: "設定",
              icon: "settings",
              onClick: () => onOpenSectionSettings(section),
            });
          }
          if (section.can_edit) {
            menuItems.push({
              label: "フォルダを作成",
              icon: "create_new_folder",
              onClick: () => onCreateFolder(section),
            });
          }

          return (
            <SectionBlock key={section.id}>
              {/* 見出し行がセクションルート選択（絞り込みなし）を兼ねる */}
              <SectionHeaderRow
                $bannerColor={section.banner_color ?? undefined}
                $selected={rootSelected}
                role="button"
                tabIndex={0}
                aria-label={`${section.title} のすべてのファイルを表示`}
                onClick={() => onSelect(section.id, null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(section.id, null);
                  }
                }}
              >
                <div className="section-header-inner">
                  <button
                    type="button"
                    className={`section-toggle${collapsed ? "" : " open"}`}
                    aria-expanded={!collapsed}
                    aria-label={`${section.title} を${collapsed ? "展開する" : "折りたたむ"}`}
                    onClick={(e) => {
                      // 矢印は開閉のみ（選択は行クリック側）
                      e.stopPropagation();
                      toggleSection(section.id);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <span className="material-symbols-rounded">
                      chevron_right
                    </span>
                  </button>
                  <h3 className="section-title heading-13 u-line-height-200">
                    {section.title}
                  </h3>
                  {menuItems.length > 0 && (
                    <span
                      className="section-menu"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu items={menuItems} size="small" />
                    </span>
                  )}
                </div>
              </SectionHeaderRow>

              {/* 各ライブラリの一番上に置く「ブックマーク」ページ
                  （セクション内の全オーディオブックマーク一覧） */}
              {!collapsed && onSelectBookmarks && (
                <TreeRow
                  $selected={bookmarksSelected}
                  $depth={0}
                  role="button"
                  tabIndex={0}
                  aria-label={`${section.title} のブックマーク一覧を表示`}
                  onClick={() => onSelectBookmarks(section.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelectBookmarks(section.id);
                    }
                  }}
                >
                  {/* フォルダより上位のページとして見せるため、開閉矢印ぶんの
                      スペーサーは置かずアイコンを行の左端から始める */}
                  <span
                    className="material-symbols-rounded"
                    style={{
                      fontSize: 16,
                      flexShrink: 0,
                      color: bookmarksSelected
                        ? "var(--soremo-blue)"
                        : "var(--soremo-light-gray)",
                    }}
                  >
                    bookmark
                  </span>
                  <span className="row-title bodytext-13 u-line-height-150">
                    ブックマーク
                  </span>
                </TreeRow>
              )}

              {!collapsed && renderFolderRows(section, tree)}
            </SectionBlock>
          );
        })}

        {canManage && (
          <AddSectionButton onClick={onCreateSection}>
            <span className="material-symbols-rounded">create_new_folder</span>
            <span className="bodytext-13">セクションを追加</span>
          </AddSectionButton>
        )}

        {sections.length === 0 && !canManage && (
          <p
            className="bodytext-13 u-text-light-gray"
            style={{ padding: "0 8px" }}
          >
            閲覧できる共有ライブラリはありません
          </p>
        )}
      </SectionsList>
    </SidebarWrapper>
  );
};

export default LibrarySidebar;
